<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { faqsApi, tutorialsApi, type Faq, type Tutorial } from '@/services/api/emsl';
import { useAppDialog } from '@/composables/useAppDialog';
import { useAppConfirm } from '@/composables/confirm';
import FaqDialog from '@/components/dialogs/FaqDialog.vue';
import TutorialDialog from '@/components/dialogs/TutorialDialog.vue';

const faqs = ref<Faq[]>([]);
const tutorials = ref<Tutorial[]>([]);
const dialog = useAppDialog();
const confirm = useAppConfirm();

async function reload(): Promise<void> {
  [faqs.value, tutorials.value] = await Promise.all([faqsApi.index(), tutorialsApi.index()]);
}

// ── FAQs ──────────────────────────────────────────────────────────────────
async function openAddFaq(): Promise<void> {
  const { result } = dialog.open<Partial<Faq>>(FaqDialog, { header: 'Add FAQ' });
  const data = await result;
  if (!data) return;
  const created = await faqsApi.create(data);
  faqs.value = [...faqs.value, created];
}

async function openEditFaq(faq: Faq): Promise<void> {
  const { result } = dialog.open<Partial<Faq>>(FaqDialog, {
    header: 'Edit FAQ',
    data: { item: faq },
  });
  const data = await result;
  if (!data) return;
  const updated = await faqsApi.update({ ...data, id: faq.id });
  faqs.value = faqs.value.map((f) => (f.id === updated.id ? updated : f));
}

async function removeFaq(faq: Faq): Promise<void> {
  const ok = await confirm({ header: 'Remove FAQ', message: 'Remove this FAQ?' });
  if (!ok) return;
  await faqsApi.remove(faq.id);
  faqs.value = faqs.value.filter((f) => f.id !== faq.id);
}

// ── Tutorials ─────────────────────────────────────────────────────────────
async function openAddTutorial(): Promise<void> {
  const { result } = dialog.open<Partial<Tutorial>>(TutorialDialog, { header: 'Add Tutorial' });
  const data = await result;
  if (!data) return;
  const created = await tutorialsApi.create(data);
  tutorials.value = [...tutorials.value, created];
}

async function openEditTutorial(t: Tutorial): Promise<void> {
  const { result } = dialog.open<Partial<Tutorial>>(TutorialDialog, {
    header: 'Edit Tutorial',
    data: { item: t },
  });
  const data = await result;
  if (!data) return;
  const updated = await tutorialsApi.update({ ...data, id: t.id });
  tutorials.value = tutorials.value.map((x) => (x.id === updated.id ? updated : x));
}

async function removeTutorial(t: Tutorial): Promise<void> {
  const ok = await confirm({ header: 'Remove Tutorial', message: `Remove "${t.topic}"?` });
  if (!ok) return;
  await tutorialsApi.remove(t.id);
  tutorials.value = tutorials.value.filter((x) => x.id !== t.id);
}

onMounted(reload);
</script>

<template>
  <section class="resource">
    <h1>Support</h1>

    <header class="resource__header">
      <h2>FAQs</h2>
      <button type="button" class="resource__cta" @click="openAddFaq">+ Add FAQ</button>
    </header>
    <ul class="resource__list">
      <li v-for="faq in faqs" :key="faq.id">
        <div class="faq-row__text">
          <strong>{{ faq.question }}</strong>
          <p>{{ faq.answer }}</p>
        </div>
        <div class="row__actions">
          <button type="button" @click="openEditFaq(faq)">Edit</button>
          <button type="button" @click="removeFaq(faq)">Remove</button>
        </div>
      </li>
    </ul>
    <p v-if="faqs.length === 0">No FAQs.</p>

    <header class="resource__header resource__header--spaced">
      <h2>Tutorials</h2>
      <button type="button" class="resource__cta" @click="openAddTutorial">+ Add Tutorial</button>
    </header>
    <ul class="resource__list">
      <li v-for="t in tutorials" :key="t.id">
        <a :href="t.link" target="_blank" rel="noopener noreferrer">{{ t.topic }}</a>
        <div class="row__actions">
          <button type="button" @click="openEditTutorial(t)">Edit</button>
          <button type="button" @click="removeTutorial(t)">Remove</button>
        </div>
      </li>
    </ul>
    <p v-if="tutorials.length === 0">No tutorials.</p>
  </section>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
h2 {
  font-size: 1.125rem;
  margin: 0;
}
.resource__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.resource__header--spaced {
  margin-top: 2rem;
}
.resource__cta {
  padding: 0.4rem 0.875rem;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font: inherit;
}
.resource__list li {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}
.resource__list li a {
  flex: 1;
  color: #374151;
  text-decoration: none;
}
.resource__list li a:hover {
  color: #2563eb;
  text-decoration: underline;
}
.faq-row__text {
  flex: 1;
}
.faq-row__text p {
  margin: 0.25rem 0 0;
  color: #4b5563;
  white-space: pre-wrap;
}
.row__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
</style>
